import { ChangeDetectionStrategy, Component, DestroyRef, input, Optional, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { connectState } from '@examdojo/angular/util';
import { LoaderComponent } from '@examdojo/ui/loader';
import { IonContent } from '@ionic/angular/standalone';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { shareOneReplay } from '@examdojo/core/rxjs';
import { ChatImageService } from '../../../base/services/chat-image.service';
import { ImageChatMessageContentStoreModel, ScrollTriggerProvider } from '../../models';
import { ImageChatMessageQuery } from '../../store';
import { ImageChatMessageService } from '../../services/image-chat-message.service';
import { ChatInputComponent } from '../chat-input/chat-input.component';
import { ImageChatMessageComponent } from '../chat-message/chat-message.component';

@Component({
  selector: 'dojo-image-chat',
  standalone: true,
  imports: [ImageChatMessageComponent, ChatInputComponent, LoaderComponent, IonContent],
  providers: [ChatImageService],
  templateUrl: './chat.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col h-full' },
  styleUrls: ['./chat.component.scss'],
})
export class ImageChatComponent {
  constructor(
    private readonly imageChatMessageQuery: ImageChatMessageQuery,
    private readonly imageChatMessageService: ImageChatMessageService,
    @Optional() private readonly scrollTriggerProvider: ScrollTriggerProvider,
    private readonly destroyRef: DestroyRef,
  ) {
    merge(
      this.scrollToBottomOnNewMessage(),
      this.scrollToBottomOnScrollTrigger(),
      this.createChatAndFetchMessagesOnPathChange(),
    )
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  readonly contentJsonPath = input.required<string>();
  readonly isDragOver = signal(false);

  readonly contentJsonPath$ = toObservable(this.contentJsonPath);

  private readonly chat = viewChild.required<IonContent>('chat');

  private readonly chatMessages$ = this.contentJsonPath$.pipe(
    distinctUntilChanged(),
    switchMap((contentJsonPath) => this.imageChatMessageQuery.selectMessagesByContentJsonPath(contentJsonPath)),
    map((messages) => messages.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())),
    shareOneReplay(),
  );

  private readonly isResponseLoading$ = toObservable(this.contentJsonPath).pipe(
    switchMap((contentJsonPath) => this.imageChatMessageQuery.selectChatIsLoadingResponse(contentJsonPath)),
  );

  private readonly isChatCreated$ = toObservable(this.contentJsonPath).pipe(
    switchMap((contentJsonPath) => this.imageChatMessageQuery.selectIsChatCreated(contentJsonPath)),
  );

  readonly state = connectState({
    chatMessages: this.chatMessages$,
    isLoading: this.imageChatMessageQuery.selectLoading(),
    isChatCreated: this.isChatCreated$,
    isResponseLoading: this.isResponseLoading$,
    isInputDisabled: combineLatest([this.isResponseLoading$, this.imageChatMessageQuery.selectLoading()]).pipe(
      map(([isResponseLoading, isLoading]) => isResponseLoading || isLoading),
    ),
  });

  sendMessage(message: ImageChatMessageContentStoreModel) {
    if (this.state.isResponseLoading || this.state.isLoading) {
      return;
    }

    this.imageChatMessageService.sendMessage(message, this.contentJsonPath());
  }

  restartChat() {
    this.imageChatMessageService
      .createReviewImageChat(this.contentJsonPath(), { force: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private scrollToBottomOnNewMessage() {
    return this.chatMessages$.pipe(
      debounceTime(20),
      tap((_) => this.scrollToBottom()),
    );
  }

  private scrollToBottomOnScrollTrigger() {
    if (!this.scrollTriggerProvider || !this.scrollTriggerProvider.scrollTrigger$) {
      return of(null);
    }

    return this.scrollTriggerProvider.scrollTrigger$.pipe(
      switchMap(() =>
        this.imageChatMessageQuery.selectLoading().pipe(
          startWith(!!this.imageChatMessageQuery.getValue().loading),
          filter((isLoading) => !isLoading),
        ),
      ),
      debounceTime(20),
      tap(() => this.scrollToBottom(0)),
    );
  }

  private scrollToBottom(duration = 250) {
    this.chat()?.scrollToBottom(duration);
  }

  private createChatAndFetchMessagesOnPathChange() {
    return this.contentJsonPath$.pipe(
      distinctUntilChanged(),
      switchMap((contentJsonPath) => this.imageChatMessageService.createReviewImageChat(contentJsonPath)),
      // switchMap((chatId) => {
      //   if (!chatId) {
      //     return of(null);
      //   }

      //   return this.imageChatMessageService.fetchMessageByChatIds([chatId]);
      // }),
      // tap((messages) => {
      //   if (messages && messages[messages.length - 1]?.created_by_role === 'ai') {
      //     // clean up loading state, if latest message is from AI
      //     this.imageChatMessageService.updateImagePropsWithStartedRequestId(null, this.contentJsonPath());
      //   }
      // }),
    );
  }
}
